import 'whatwg-fetch';
import 'babel-polyfill';
import { OptimizedResizeInstance } from 'js/components/Util/OptimizedResize.js';
import { SpinnerModal } from 'js/components/SpinnerModal/SpinnerModal.js';
import { LocationHours } from 'js/components/LocationHours/index--override.js';
import { Maps } from 'js/components/Maps/index.js';

const Raven = require('raven-js/dist/raven.js');
Raven.config('https://13d7b1cf5db9462a8a6121dfd4d032c5@sentry.io/751790').install();
const URI = require('urijs');

import { Form } from 'js/components/Cobalt/Locator/Form.js';
import { FilterModal } from 'js/components/Cobalt/Locator/FilterModal.js';

import { HTML5Geolocation } from 'js/components/Util/Html5Geolocation.js';
HTML5Geolocation.initClass();

import { ReverseGeolocation } from 'js/components/Util/ReverseGeolocation.js';
ReverseGeolocation.initClass();

let queryIsEmpty = function(query) {
  return query == "?" || query == "";
}

export class Locator {
  constructor(el, form, renderer, stateChangeCallback, config) {
    this.el = el;
    this.inputEl = this.el.querySelector('.js-locator-input');
    this.resultsContainer = this.el.querySelector('.js-locator-resultsContainer');
    this.form = form;
    this.renderer = renderer;
    this.stateChangeCallback = stateChangeCallback || (() => {});
    this.config = config;
    this.centerPin = null;

    this.spinner = new SpinnerModal();
    this.payPointFilterEl = this.form.el.querySelector('.Locator-payPointFilter');
    this.payPointFilterInputs = Array.from(this.form.el.querySelectorAll('.js-paypoint-filter'));

    this.form.bindSubmitHandler(this.submitHandler.bind(this));
    window.onpopstate = this.popHandler.bind(this);

    for (const input of this.payPointFilterInputs) {
      input.addEventListener('change', this.payPointFilterChangeHandler.bind(this));
    }

    const geolocateButton = this.form.el.querySelector('.js-locator-geolocateTrigger');
    if (geolocateButton) {
      geolocateButton.addEventListener('click', () => {this.geolocate(this.inputEl)});
    }

    this.onLoad();
    if (this.config.locationOnLoad && window.location.href.indexOf('?q') === -1) {
      navigator.permissions.query({name: 'geolocation'}).then(result => {
        if (result.state == 'granted') {
          this.geolocate(this.inputEl);
        }
        else {
          this.ipLocate(this.inputEl)
        }
      })
    }
  }

  popHandler(evt) {
    this.renderer.render(evt.state);
    this.setPayPointFilterVisibility(this.hasSearchQuery(new URI(window.location.search).search(true)));
    this.form.deserialize(window.location.search);
    this.stateChangeCallback();
  }

  getInitialData() {
    const initialDataEl = this.el.querySelector('.js-locator-initialData');
    return initialDataEl ? JSON.parse(initialDataEl.innerHTML) : {};
  }

  hasSearchQuery(queryMap) {
    return Boolean(queryMap.q || queryMap.what3WordsAddress);
  }

  getFormQuery() {
    const inputValue = this.inputEl.value.trim();
    let query = this.form.serialize();

    if (inputValue && this.validateWhat3WordsInput(inputValue)) {
      const queryParams = new URI(`?${query}`);
      queryParams.removeSearch('q');
      queryParams.addSearch({ what3WordsAddress: inputValue });
      query = queryParams.query();
    }

    return query;
  }

  syncUrlToQuery(query, historyMethod = 'pushState', state = {}) {
    let currentURI = new URI(window.location.href).search('');
    let formQueryMap = new URI(`?${query}`).search(true);
    currentURI.setSearch(formQueryMap);
    window.history[historyMethod](state, "", currentURI.toString());

    return { currentURI, formQueryMap };
  }

  async payPointFilterChangeHandler() {
    if (this.form.isDeserializing) {
      return;
    }

    const query = this.getFormQuery();
    const { currentURI, formQueryMap } = this.syncUrlToQuery(query);

    if (!this.hasSearchQuery(formQueryMap)) {
      const initialData = this.getInitialData();
      window.history.replaceState(initialData, "", currentURI.toString());
      this.renderer.render(initialData);
      this.stateChangeCallback();
      return;
    }

    try {
      const data = await this.searchAndRender(query);
      window.history.replaceState(data, "", currentURI.toString());
      wa_action();
    } catch(err) {
      console.log("err: ", err);
    }

    this.stateChangeCallback();
  }

  setPayPointFilterVisibility(isVisible) {
    if (!this.payPointFilterEl) {
      return;
    }

    this.payPointFilterEl.classList.toggle('Locator-payPointFilter--hidden', !isVisible);
    this.payPointFilterEl.setAttribute('aria-hidden', String(!isVisible));
  }

  async setQueryParams(position, input, isHTML5) {
    const currentUrl = `${window.location.pathname}`;
    const queryString = `q=${position.latitude},${position.longitude}`;
    let results = null;
    let formValues = this.form.serialize();
    let params = new URI(`?${formValues}`);
    params.removeSearch('q');
    try {
      results = await this.search(queryString);
      const queryLocation = results.queryLocation;
      if (queryLocation) {
        input.value = `${queryLocation.city}, ${queryLocation.state} ${queryLocation.zip}`;
      } else {
        input.value = `${position.latitude},${position.longitude}`;
      }
      params.addSearch({ q : `${position.latitude},${position.longitude}` });
      window.history.pushState(results, "", `${currentUrl}${params.toString()}`);
      this.renderer.render(results);
      this.setPayPointFilterVisibility(true);
      if (isHTML5) {
        this.spinner.hideSpinner();
      }
    } catch(err) {
      if (isHTML5) {
        console.log("HTML5 Geolocation Error: ", err);
        Raven.captureException(err, {
          extra: {
            details: 'HTML5 Geolocation: Error searching after geolocation',
            queryString: queryString
          }
        });
      }
      else {
        console.log("IP Geolocation Error: ", err);
          Raven.captureException(err, {
            extra: {
              details: 'IP Geolocation Error: Error searching after geolocation',
              queryString: queryString
            }
        });
      }
    }
  }

  svgPin() {
    return `
      <svg width="39" height="50" viewBox="0 0 39 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.5 0C8.775 0 0 8.73524 0 19.4116C0 27.8234 11.7 42.7056 16.9 48.8526C18.2 50.1468 20.15 50.4703 21.45 49.1762L21.775 48.8526C27.3 42.7056 39 27.8234 39 19.4116C39 8.73524 30.225 0 19.5 0Z" fill="#011546"/>
        <circle cx="20" cy="19" r="7" fill="white" stroke="white" stroke-width="4"/>
      </svg>
    `;
  }

  addCenterPin(position) {
    this.removeCenterPin(); //remove previous center pin
    if (Maps.instances && Maps.instances[0] && Maps.instances[0].map) {     
      const map = Maps.instances[0].map; 
      const iconUrl = `data:image/svg+xml;charset=utf8,${encodeURIComponent(this.svgPin())}`;
      this.centerPin = new google.maps.Marker({
        position: {lat: position.latitude, lng: position.longitude},
        map: map,
        title: 'Selected search location',
        ariaLabel: 'Selected search location',
        icon: iconUrl
      });
      this.centerPin.setTitle('Selected search location');
    }
  }

  removeCenterPin() {
    if(this.centerPin) {
      this.centerPin.setMap(null);
      this.centerPin = null;
    }
  }

  addPinAtLocationCenter() {
    const geocoder = new google.maps.Geocoder();
    const context = this;
    geocoder.geocode({ address: this.inputEl.value }, function(results, status) {
      if (status === google.maps.GeocoderStatus.OK && results.length > 0) {
        const location = results[0].geometry.location;
        context.addCenterPin({latitude: location.lat(), longitude: location.lng()})
      }
    })
  }

  validateWhat3WordsInput(input) {
    // regex to check if the input is a what3word address as per the below link
    // https://developer.what3words.com/tutorial/detecting-if-text-is-in-the-format-of-a-3-word-address
    const regex = /^\/{0,}(?:[^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+|[^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+([\u0020\u00A0][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+){1,3}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+([\u0020\u00A0][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+){1,3}[.｡。･・︒។։။۔።।][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+([\u0020\u00A0][^0-9`~!@#$%^&*()+\-_=[{\]}\\|'<,.>?/";:£§º©®\s]+){1,3})$/;
    return regex.test(input);
  }

  async geolocate(input) {
    this.spinner.showSpinner();
    HTML5Geolocation.getCurrentLocation(async (position) => {
      if (position.hasOwnProperty('latitude') && position.hasOwnProperty('longitude')) {
        this.setQueryParams(position, input, true);
        this.addCenterPin(position);
      } else {
        console.log("HTML5 Geolocation: Error no position found.");
      }
    }, () => {
      this.spinner.hideSpinner();
      if (this.config.locationOnLoad) {
        this.ipLocate(input);
      }
      console.log("HTML5 Geolocation: Error no position found.");
    });
  }

  async ipLocate(input) {
    ReverseGeolocation.getCurrentLocation(async (position) => {
      if (position.hasOwnProperty('latitude') && position.hasOwnProperty('longitude')) {
        this.setQueryParams(position, input);
        this.addCenterPin(position);
      }
    }, () => {
      console.log("IP Geolocation Error: unable to locate user.");
    });
  }

  async onLoad() {
    const currentUrl = `${window.location.pathname}`;
    const currentQuery = window.location.search;
    const locatorParamsEl = this.el.querySelector('.js-locator-params');
    this.templateParams = locatorParamsEl ? JSON.parse(locatorParamsEl.innerHTML) : {};
    Raven.setExtraContext({
      site: this.templateParams.siteInternalHostName ? this.templateParams.siteInternalHostName : 'unknown'
    });
    if (queryIsEmpty(currentQuery)) {
      this.setPayPointFilterVisibility(false);
      const data = this.getInitialData();
      window.history.replaceState(data,"", `${currentUrl}`);
      return;
    }

    this.form.deserialize(currentQuery);
    this.setPayPointFilterVisibility(false);
    const currentURI = new URI(window.location.href).search(true);

    if (!this.hasSearchQuery(currentURI)) {
      const data = this.getInitialData();
      window.history.replaceState(data, "", `${currentUrl}${currentQuery}`);
      this.renderer.render(data);
      this.stateChangeCallback();
      return;
    }

    window.history.replaceState({},"", `${currentUrl}${currentQuery}`);
    let data = null;

    if(currentURI.what3WordsAddress) {
      this.inputEl.value = currentURI.what3WordsAddress
    }
    nbs_ddl.action = nbs_ddl.action || [];
    const analyticsData = {
      'type': 'user input',
      'user_input': {
        'type': this.inputEl.type,
        'identifier': currentURI.q
      }
    };

    try {
      data = await this.submitAndRender();
      window.history.replaceState(data, "", `${currentUrl}${currentQuery}`);
      this.setPayPointFilterVisibility(true);
      wa_action();
    } catch(err) {
      console.log("err: ", err);
      Raven.captureException(err, {
        extra: {
          details: 'Initial search after deserializing from url',
          queryString: currentQuery
        }
      });
      analyticsData.user_input = {
        'message': err.toString()
      };
    }
    this.stateChangeCallback();
    nbs_ddl.action.unshift(analyticsData);
  }

  async submitHandler(evt) {
    if (evt) evt.preventDefault();

    // Hack to hide the mobile keyboard
    this.inputEl.setAttribute('readonly', true);
    window.setTimeout(() => {
      this.inputEl.blur();
      this.inputEl.removeAttribute('readonly');
    }, 100);
    // if there's a prefix match option in the autocomplete, and that's what the user
    // has selected, we don't want to call setupQuery as that takes the first actual
    // autocomplete option and uses that as the search value
    const prefixMatch = this.inputEl.dataset.prefixMatch;
    if (prefixMatch != this.inputEl.value) {
      await this.setupQuery(this.form.el);
    }
    // after every search remove the data attribute so we don't mess with future searches
    this.inputEl.removeAttribute('data-prefix-match');

    
    let query = this.getFormQuery();
    let { currentURI, formQueryMap } = this.syncUrlToQuery(query);

    let data = null;
    nbs_ddl.action = nbs_ddl.action || [];
    const analyticsData = {
      'type': 'user input',
      'user_input': {
        'type': this.inputEl.type,
        'identifier': formQueryMap.q
      }
    };
    
    try {
      data = await this.submitAndRender();
      window.history.replaceState(data, "", currentURI.toString());
      this.setPayPointFilterVisibility(this.hasSearchQuery(formQueryMap));
      wa_action();
    } catch(err) {
      console.log("err: ", err);
      Raven.captureException(err, {
        extra: {
          details: 'Standard search',
          queryString: query
        }
      });
      analyticsData.user_input = {
        'message': err.toString()
      };
    }

    nbs_ddl.action.unshift(analyticsData);
  }

  async setupQuery(form) {
    return new Promise((resolve, reject) => {
      Yext.autocompleteService.getPlacePredictions({
        input: form.q.value.toLowerCase(),
        types: ['geocode'],
        componentRestrictions: {
          country: 'gb'
        }
      },
      (predictions, status) => {

        // if results are found, swap in the first geocoded results
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          form.q.value = predictions[0].description;
        }

        resolve();
      });
    });
  }

  async submitAndRender() {
    return this.searchAndRender(this.getFormQuery());
  }

  async searchAndCallback(query, cb) {
    try {
      Yext.Analytics.send({eventType: 'search'});
    } catch(err) {
      console.log(err);
    }
    this.spinner.showSpinner();
    const startTime = performance.now();
    let [results, prefixResults] = await Promise.all([this.search(query), this.searchPrefix(query)]);
    this.addPinAtLocationCenter();
    // Join the two result sets and remove duplicates
    const prefixResultsIds = prefixResults.locations.map(location => location.loc.id);
    results.locations = prefixResults.locations.concat(
      results.locations.filter(location => !prefixResultsIds.includes(location.loc.id))
    );
    if (cb) {
      cb(results)
    }
    const finishTime = performance.now();
    if ((finishTime - startTime) > 500) {
      this.spinner.hideSpinner();
    } else {
      window.setTimeout(this.spinner.hideSpinner.bind(this.spinner), 500 - (finishTime - startTime));
    }
    return results
  }

  async searchAndRender(query) {
    return this.searchAndCallback(query, this.renderer.render.bind(this.renderer));
  }

  async search(query) {
    const fetchInit = {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
      }
    };
    try {
      const response = await fetch(`${this.form.getAction()}?${query}`, fetchInit);
      return response.json();
    } catch(err) {
      Raven.captureException(err, {
        extra: {queryString: query,}
      });
      return Promise.reject(e)
    }
  }

  async searchPrefix(query) {
    return this.search(`prefix=${query.replace('q=', '').replace('%2C+UK', '')}`)
  }
}
